// ============================================================================
// CLEO'S SECRET — PrepCurriculum
// File: src/pages/PrepCurriculum.jsx
//
// The ten-day curriculum index page. Patient sees their daily modules,
// what's open, what's still locked, and which module is in focus today.
//
// Three card variants:
//   • FeaturedModuleCard — large card for today's focus, BR left bar
//   • SmallModuleCard    — list-grid items for the other 7 days
//   • ThresholdModuleCard — Day 8 special "threshold" treatment on OBS
//
// Pattern: PATIENT-FACING EDITORIAL. Cream PAP page with SAND header strip,
// segmented progress spine, then three module sections, closing flourish
// with AnkhMed + italic premise quote.
//
// Already on the May 2026 design system; this pass:
//   • Removes the inline SVG ankh (the threshold card was drawing one from
//     rects); replaced with the proper AnkhSm atom
//   • Replaces arrow glyphs in button copy with clean text
//   • Module h3s changed from T.label (uppercase Cinzel) to proper Cormorant
//     headlines — they're titles, not labels
//   • "✓ Complete" → Cinzel "COMPLETE" in GREEN (palette signal, not glyph)
//   • "—" em dash on rest day in the spine label → italic Cinzel "R"
//   • Sentence case throughout
//
// API endpoints preserved:
//   • /api/get-prep-module (loads curriculum state)
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  P, FF, FS,
  AnkhMed, AnkhSm, BtnGhost, Spinner,
} from '../lib/design-tokens';

// ───────── Helpers ─────────
function romanNumeral(n) {
  var map = ['I','II','III','IV','V','VI','VII','VIII','IX','X','XI','XII'];
  return map[n - 1] || String(n);
}

function formatSecondsAsMin(sec) {
  if (!sec) return '4-6 min';
  return Math.round(sec / 60) + ' min';
}

// ═════════════════════════════════════════════════════════════════════════
// PrepCurriculum
// ═════════════════════════════════════════════════════════════════════════
export default function PrepCurriculum(props) {
  var onNavigate = props.onNavigate;
  var email = props.email;
  var firstName = props.firstName;

  var dataState = useState(null);
  var data = dataState[0];
  var setData = dataState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  useEffect(function() {
    if (!email) { setLoading(false); return; }

    fetch('/api/get-prep-module', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email }),
    })
      .then(function(res) {
        if (!res.ok) return res.json().then(function(e) { throw new Error(e.error || 'Could not load curriculum'); });
        return res.json();
      })
      .then(function(result) { setData(result); setLoading(false); })
      .catch(function(err) { setError(err.message); setLoading(false); });
  }, [email]);

  // ────────────────────────────────────────────────────────────────────────
  // LOADING STATE
  // ────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        background: P.PAP,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Spinner color={P.GO} size={48} />
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // ERROR STATE
  // ────────────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div style={{
        background: P.PAP,
        minHeight: '100vh',
        padding: 'clamp(80px, 12vw, 140px) clamp(24px, 3vw, 40px)',
        textAlign: 'center',
        fontFamily: FF,
      }}>
        <div style={{ maxWidth: 580, margin: '0 auto' }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.RED,
            marginBottom: 22,
          }}>Could not load</div>
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(20px, 2vw, 24px)',
            fontStyle: 'italic',
            color: P.TX,
            lineHeight: 1.55,
            marginBottom: 'clamp(32px, 3.6vw, 44px)',
          }}>{error}</p>
          <BtnGhost onClick={function() { if (onNavigate) onNavigate('portal'); }}>Back to portal</BtnGhost>
        </div>
      </div>
    );
  }

  var currentDay = (data && data.currentDay) || 1;
  var modules = (data && data.modules) || [];
  var totalDays = 10;
  var displayDay = Math.min(Math.max(currentDay, 1), totalDays);
  var modulesViewed = modules.filter(function(m) { return !!m.viewedAt; }).length;

  function chooseFocusModule() {
    if (modules.length === 0) return null;
    var unlocked = modules.filter(function(m) { return m.isUnlocked; });
    if (unlocked.length === 0) return null;
    var nextInProgress = unlocked.find(function(m) { return !m.hasReflection; });
    if (nextInProgress) return nextInProgress;
    return unlocked[unlocked.length - 1];
  }

  var focusModule = chooseFocusModule();
  var otherModules = modules.filter(function(m) {
    return !focusModule || m.sequenceNumber !== focusModule.sequenceNumber;
  });
  var thresholdModule = otherModules.find(function(m) { return m.sequenceNumber === 8; });
  var weekModules = otherModules.filter(function(m) { return m.sequenceNumber !== 8; });

  function openModule(m) {
    if (!m.isUnlocked) return;
    if (onNavigate) onNavigate('prep-module', { moduleSequenceNumber: m.sequenceNumber });
  }

  // ════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div style={{
      background: P.PAP,
      minHeight: '100vh',
      fontFamily: FF,
      paddingBottom: 'clamp(64px, 8vw, 112px)',
    }}>

      {/* ═════════ Editorial header ═════════ */}
      <header style={{
        background: P.SAND,
        borderBottom: '1px solid ' + P.SAND3,
        padding: 'clamp(64px, 8vw, 96px) clamp(24px, 3vw, 40px) clamp(44px, 5.4vw, 64px)',
        textAlign: 'center',
      }}>
        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(20px, 2.4vw, 30px)',
          fontStyle: 'italic',
          fontWeight: 400,
          color: P.BR2,
          lineHeight: 1.35,
          letterSpacing: '-0.005em',
          margin: '0 0 22px',
        }}>The ten days before your consultation.</p>

        <h1 style={{
          fontFamily: FF,
          fontSize: 'clamp(34px, 4.6vw, 56px)',
          fontWeight: 400,
          color: P.INK,
          lineHeight: 1.1,
          letterSpacing: '-0.014em',
          margin: '0 0 clamp(18px, 2vw, 24px)',
        }}>
          {firstName
            ? <>{firstName}, your <span style={{ fontStyle: 'italic', color: P.BR2 }}>curriculum</span> awaits.</>
            : <>Your <span style={{ fontStyle: 'italic', color: P.BR2 }}>curriculum</span> awaits.</>}
        </h1>

        <div style={{
          width: 'clamp(140px, 14vw, 180px)',
          height: 1,
          background: P.GO,
          margin: '0 auto clamp(20px, 2vw, 26px)',
        }} />

        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(15px, 1.4vw, 17px)',
          color: P.TL,
          lineHeight: 1.7,
          maxWidth: 560,
          margin: '0 auto',
        }}>Eight short modules. One per day. Taught by your GP.</p>
      </header>

      {/* ═════════ Segmented progress rail ═════════ */}
      <section style={{
        maxWidth: 820,
        margin: 'clamp(40px, 5vw, 64px) auto 0',
        padding: '0 clamp(24px, 3vw, 40px)',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 18,
          marginBottom: 22,
          flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(11px, 0.95vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.34em',
              textTransform: 'uppercase',
              color: P.BR2,
              marginBottom: 8,
            }}>Day {displayDay} of {totalDays}</div>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(17px, 1.8vw, 22px)',
              fontStyle: 'italic',
              color: P.TX,
              lineHeight: 1.4,
            }}>
              {modulesViewed === 0 && 'The first module is open.'}
              {modulesViewed > 0 && modulesViewed < modules.length && 'You have made a start.'}
              {modulesViewed === modules.length && modules.length > 0 && 'You are ready for your consultation.'}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(34px, 4vw, 44px)',
              fontWeight: 400,
              color: P.BR,
              lineHeight: 1,
              letterSpacing: '-0.015em',
            }}>
              {modulesViewed}
              <span style={{
                color: P.SAND3,
                fontSize: 'clamp(20px, 2.3vw, 26px)',
              }}>/{modules.length}</span>
            </div>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(11px, 0.95vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.34em',
              textTransform: 'uppercase',
              color: P.BR2,
              marginTop: 6,
            }}>modules opened</div>
          </div>
        </div>

        {/* 10-segment spine */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(10, 1fr)',
          gap: 4,
        }}>
          {Array.from({ length: 10 }).map(function(_, idx) {
            var dayNum = idx + 1;
            var isCurrent = dayNum === displayDay;
            var isPast = dayNum < displayDay;
            return (
              <div key={idx} style={{
                height: 6,
                background: (isCurrent || isPast) ? P.GO : P.SAND2,
                transition: 'background 0.4s',
              }} />
            );
          })}
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(10, 1fr)',
          gap: 4,
          marginTop: 10,
        }}>
          {Array.from({ length: 10 }).map(function(_, idx) {
            var dayNum = idx + 1;
            var isCurrent = dayNum === displayDay;
            var isPast = dayNum < displayDay;
            var isRestDay = dayNum === 8;
            return (
              <div key={idx} style={{
                textAlign: 'center',
                fontFamily: FF,
                fontSize: 'clamp(11px, 0.95vw, 13px)',
                letterSpacing: '0.28em',
                color: isCurrent ? P.INK : (isPast ? P.BR2 : P.TL),
                fontWeight: 700,
                fontStyle: isRestDay ? 'italic' : 'normal',
              }}>
                {isRestDay ? (
                  <span style={{
                    fontFamily: FF,
                    fontStyle: 'italic',
                    color: P.BR,
                  }}>R</span>
                ) : dayNum}
              </div>
            );
          })}
        </div>
      </section>

      {/* ═════════ Today / Featured module ═════════ */}
      {focusModule && (
        <section style={{
          maxWidth: 820,
          margin: 'clamp(48px, 6vw, 72px) auto 0',
          padding: '0 clamp(24px, 3vw, 40px)',
        }}>
          <FeaturedModuleCard
            module={focusModule}
            currentDay={displayDay}
            onClick={function() { openModule(focusModule); }}
          />
        </section>
      )}

      {/* ═════════ Week modules grid ═════════ */}
      {weekModules.length > 0 && (
        <section style={{
          maxWidth: 820,
          margin: 'clamp(36px, 4.4vw, 52px) auto 0',
          padding: '0 clamp(24px, 3vw, 40px)',
        }}>
          <SectionDivider label="Across the ten days" />
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 14,
          }}>
            {weekModules.map(function(m) {
              return (
                <SmallModuleCard
                  key={m.sequenceNumber}
                  module={m}
                  onClick={function() { openModule(m); }}
                />
              );
            })}
          </div>
        </section>
      )}

      {/* ═════════ Threshold module (Day 8) ═════════ */}
      {thresholdModule && (
        <section style={{
          maxWidth: 820,
          margin: 'clamp(32px, 4vw, 48px) auto 0',
          padding: '0 clamp(24px, 3vw, 40px)',
        }}>
          <SectionDivider label="The threshold" />
          <ThresholdModuleCard
            module={thresholdModule}
            onClick={function() { openModule(thresholdModule); }}
          />
        </section>
      )}

      {/* ═════════ Closing ornament ═════════ */}
      <div style={{ textAlign: 'center', marginTop: 'clamp(64px, 8vw, 96px)' }}>
        <AnkhMed color={P.GO} opacity={0.75} />
        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(16px, 1.8vw, 22px)',
          fontStyle: 'italic',
          color: P.TL,
          lineHeight: 1.5,
          maxWidth: 560,
          margin: 'clamp(24px, 2.6vw, 32px) auto 0',
          padding: '0 clamp(24px, 3vw, 40px)',
        }}>&ldquo;Cleopatra studied the science of her own body. So can you.&rdquo;</p>
      </div>

      {/* Back nav */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginTop: 'clamp(36px, 4vw, 52px)',
      }}>
        <BtnGhost onClick={function() { if (onNavigate) onNavigate('portal'); }}>Back to portal</BtnGhost>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// SectionDivider
// ═════════════════════════════════════════════════════════════════════════
function SectionDivider({ label }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      marginBottom: 22,
    }}>
      <span style={{
        fontFamily: FF,
        fontSize: 'clamp(11px, 0.95vw, 13px)',
        fontWeight: 700,
        letterSpacing: '0.34em',
        textTransform: 'uppercase',
        color: P.BR2,
        whiteSpace: 'nowrap',
      }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: P.SAND3 }} />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// FeaturedModuleCard — today's focus
// ═════════════════════════════════════════════════════════════════════════
function FeaturedModuleCard({ module, currentDay, onClick }) {
  var hoverState = useState(false);
  var hover = hoverState[0];
  var setHover = hoverState[1];

  var isToday = module.unlockDay === currentDay;
  var stateLabel = isToday ? 'Today' : (module.hasReflection ? 'Recap' : module.viewedAt ? 'Continue' : 'Begin');
  var cta = module.hasReflection ? 'Read again' : module.viewedAt ? 'Continue' : 'Begin';

  return (
    <div
      onClick={onClick}
      onMouseEnter={function() { setHover(true); }}
      onMouseLeave={function() { setHover(false); }}
      style={{
        background: P.SAND,
        border: '1px solid ' + P.GO,
        borderLeft: '3px solid ' + P.GO,
        padding: 'clamp(28px, 4vw, 44px)',
        position: 'relative',
        cursor: 'pointer',
        transition: 'all 0.25s ease',
        boxShadow: hover ? '0 14px 32px -16px rgba(29,17,22,0.4)' : '0 2px 0 rgba(201,169,97,0.08)',
        transform: hover ? 'translateY(-2px)' : 'none',
      }}>
      {/* State badge top-right */}
      <div style={{
        position: 'absolute',
        top: 22,
        right: 26,
        fontFamily: FF,
        fontSize: 'clamp(10px, 0.85vw, 12px)',
        fontWeight: 700,
        letterSpacing: '0.34em',
        textTransform: 'uppercase',
        color: P.BR2,
        background: P.PAP,
        border: '1px solid ' + P.GO,
        padding: '5px 12px',
      }}>{stateLabel}</div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        gap: 'clamp(20px, 3vw, 40px)',
        alignItems: 'center',
      }}>
        {/* Roman numeral */}
        <div style={{ textAlign: 'center', minWidth: 92 }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(54px, 7.2vw, 84px)',
            fontWeight: 400,
            color: P.BR,
            lineHeight: 1,
            letterSpacing: '-0.02em',
          }}>{romanNumeral(module.sequenceNumber)}</div>
          <div style={{
            width: 36,
            height: 1,
            background: P.GO,
            opacity: 0.75,
            margin: '14px auto 0',
          }} />
        </div>

        {/* Title + subtitle */}
        <div>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 12,
          }}>Day {module.unlockDay} &middot; {formatSecondsAsMin(module.videoDurationSeconds)}</div>

          <h2 style={{
            fontFamily: FF,
            fontSize: 'clamp(20px, 2.4vw, 28px)',
            fontWeight: 500,
            color: P.INK,
            lineHeight: 1.2,
            letterSpacing: '-0.008em',
            margin: '0 0 12px',
            paddingRight: 100,
          }}>{module.title}</h2>

          {module.subtitle && (
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(15px, 1.6vw, 19px)',
              fontStyle: 'italic',
              color: P.BR2,
              lineHeight: 1.5,
              margin: 0,
            }}>{module.subtitle}</p>
          )}
        </div>

        {/* CTA button */}
        <div>
          <button
            onClick={function(e) { e.stopPropagation(); onClick(); }}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(11px, 0.95vw, 12px)',
              fontWeight: 600,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '16px 30px',
              background: hover ? P.BR : P.INK,
              color: P.PAP,
              border: 'none',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'background 0.25s',
            }}>{cta}</button>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// SmallModuleCard — list-grid item for the other 7 modules
// ═════════════════════════════════════════════════════════════════════════
function SmallModuleCard({ module, onClick }) {
  var hoverState = useState(false);
  var hover = hoverState[0];
  var setHover = hoverState[1];

  var unlocked = module.isUnlocked;
  var viewed = !!module.viewedAt;
  var hasReflection = !!module.hasReflection;

  // Left bar color signals state (hasReflection > viewed > unlocked > locked)
  var leftBarColor = hasReflection ? P.BR2 :
                     viewed ? P.GO :
                     unlocked ? P.BR : P.SAND3;

  var numeralColor = hasReflection ? P.BR2 : (unlocked ? P.BR : P.TL);

  return (
    <div
      onClick={unlocked ? onClick : undefined}
      onMouseEnter={function() { setHover(true); }}
      onMouseLeave={function() { setHover(false); }}
      style={{
        background: unlocked && hover ? P.PAP : P.SAND,
        border: '1px solid ' + (unlocked ? P.SAND3 : 'rgba(200,184,136,0.6)'),
        borderLeft: '3px solid ' + leftBarColor,
        padding: 'clamp(20px, 2.4vw, 28px)',
        cursor: unlocked ? 'pointer' : 'default',
        opacity: unlocked ? 1 : 0.7,
        transition: 'all 0.2s ease',
        display: 'grid',
        gridTemplateColumns: '60px 1fr',
        gap: 18,
        alignItems: 'flex-start',
        boxShadow: unlocked && hover ? '0 6px 16px -8px rgba(201,169,97,0.3)' : 'none',
      }}>
      {/* Roman numeral */}
      <div style={{
        fontFamily: FF,
        fontSize: 'clamp(36px, 3.8vw, 44px)',
        fontWeight: 400,
        color: numeralColor,
        lineHeight: 1,
        letterSpacing: '-0.02em',
        textAlign: 'center',
        opacity: unlocked ? 1 : 0.7,
      }}>{romanNumeral(module.sequenceNumber)}</div>

      {/* Title block */}
      <div>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(11px, 0.95vw, 13px)',
          fontWeight: 700,
          letterSpacing: '0.34em',
          textTransform: 'uppercase',
          color: unlocked ? P.BR2 : P.TL,
          marginBottom: 6,
        }}>Day {module.unlockDay} &middot; {formatSecondsAsMin(module.videoDurationSeconds)}</div>

        <h3 style={{
          fontFamily: FF,
          fontSize: 'clamp(16px, 1.5vw, 18px)',
          fontWeight: 500,
          color: unlocked ? P.INK : P.TL,
          lineHeight: 1.25,
          letterSpacing: '-0.005em',
          margin: '0 0 6px',
        }}>{module.title}</h3>

        {module.subtitle && (
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(13px, 1.15vw, 14px)',
            fontStyle: 'italic',
            color: unlocked ? P.BR : P.TF,
            lineHeight: 1.55,
            margin: 0,
          }}>{module.subtitle}</p>
        )}

        {/* Status footer */}
        {unlocked && !viewed && (
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginTop: 14,
          }}>{hover ? 'Open' : 'Available'}</div>
        )}
        {unlocked && viewed && hasReflection && (
          <div style={{
            display: 'inline-block',
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.GREEN,
            background: P.GREEN_BG,
            border: '1px solid ' + P.GREEN,
            padding: '4px 10px',
            marginTop: 14,
          }}>Complete</div>
        )}
        {unlocked && viewed && !hasReflection && (
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginTop: 14,
          }}>In progress</div>
        )}
        {!unlocked && (
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 1vw, 12px)',
            fontStyle: 'italic',
            color: P.TF,
            marginTop: 14,
          }}>Unlocks day {module.unlockDay}</div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// ThresholdModuleCard — Day 8, special OBS treatment
// ═════════════════════════════════════════════════════════════════════════
function ThresholdModuleCard({ module, onClick }) {
  var hoverState = useState(false);
  var hover = hoverState[0];
  var setHover = hoverState[1];

  var unlocked = module.isUnlocked;

  return (
    <div
      onClick={unlocked ? onClick : undefined}
      onMouseEnter={function() { setHover(true); }}
      onMouseLeave={function() { setHover(false); }}
      style={{
        background: P.OBS,
        borderLeft: '3px solid ' + P.GO,
        padding: 'clamp(28px, 3.8vw, 40px)',
        cursor: unlocked ? 'pointer' : 'default',
        transition: 'all 0.25s',
        opacity: unlocked ? 1 : 0.9,
        boxShadow: unlocked && hover ? '0 12px 32px -14px rgba(0,0,0,0.55)' : 'none',
        transform: unlocked && hover ? 'translateY(-2px)' : 'none',
      }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '90px 1fr auto',
        gap: 'clamp(20px, 3vw, 36px)',
        alignItems: 'center',
      }}>
        {/* Roman numeral */}
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(44px, 5.4vw, 64px)',
            fontWeight: 400,
            color: P.GO2,
            lineHeight: 1,
            letterSpacing: '-0.02em',
          }}>{romanNumeral(module.sequenceNumber)}</div>
        </div>

        {/* Title block */}
        <div>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.GO2,
            marginBottom: 12,
          }}>Day {module.unlockDay} &middot; {formatSecondsAsMin(module.videoDurationSeconds)}</div>

          <h3 style={{
            fontFamily: FF,
            fontSize: 'clamp(18px, 2vw, 24px)',
            fontWeight: 500,
            color: P.PAP,
            lineHeight: 1.2,
            letterSpacing: '-0.008em',
            margin: '0 0 10px',
          }}>{module.title}</h3>

          {module.subtitle && (
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(15px, 1.6vw, 18px)',
              fontStyle: 'italic',
              color: P.GO2,
              lineHeight: 1.5,
              margin: 0,
            }}>{module.subtitle}</p>
          )}
        </div>

        {/* Ankh mark (replacing the inline rect-based SVG ankh) */}
        <div style={{ textAlign: 'center' }}>
          {unlocked ? (
            <AnkhSm color={P.GO2} opacity={0.85} size={26} />
          ) : (
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(11px, 0.95vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.34em',
              textTransform: 'uppercase',
              color: P.GO2,
            }}>Day {module.unlockDay}</div>
          )}
        </div>
      </div>
    </div>
  );
}
